import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../config/api';
import moment from 'moment';
import PageNavbar from './PageNavbar';
import AppIcon from './icons/AppIcon';
import OrderSearch from './OrderSearch';
import { formatDateDisplay } from '../utils/formatDate';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import './AdminAnalytics.css';

const PIE_COLORS = ['#1a1a1a', '#b8860b', '#d4af37', '#78716c', '#57534e', '#c9a227', '#5c1520', '#a3823a'];

const DATE_PRESETS = [
  { id: 'today', label: 'Today', range: () => [moment(), moment()] },
  { id: '7d', label: '7 days', range: () => [moment().subtract(6, 'days'), moment()] },
  { id: 'mtd', label: 'This month', range: () => [moment().startOf('month'), moment().endOf('month')] },
  { id: 'last', label: 'Last month', range: () => [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')] },
  { id: '30d', label: '30 days', range: () => [moment().subtract(29, 'days'), moment()] }
];

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const formatCurrencyExact = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ChartTooltip = ({ active, payload, label, labelDate }) => {
  if (!active || !payload?.length) return null;
  const date = labelDate || payload[0]?.payload?.date;
  return (
    <div className="bi-tooltip">
      <div className="bi-tooltip__label">
        {date ? formatDateDisplay(date) : label}
      </div>
      {payload.map((p) => (
        <div key={p.dataKey} className="bi-tooltip__row">
          <span>{p.name}</span>
          <strong>
            {p.dataKey === 'orders' || p.dataKey === 'orderCount' || p.dataKey === 'lineCount'
              ? p.value
              : formatCurrencyExact(p.value)}
          </strong>
        </div>
      ))}
    </div>
  );
};

const AdminAnalytics = () => {
  const [searchParams] = useSearchParams();
  const presetFromUrl = searchParams.get('preset');
  const initialPreset =
    presetFromUrl && DATE_PRESETS.some((p) => p.id === presetFromUrl) ? presetFromUrl : 'mtd';
  const initialRange = DATE_PRESETS.find((p) => p.id === initialPreset).range();

  const [sourceTab, setSourceTab] = useState('oms');
  const [preset, setPreset] = useState(initialPreset);
  const [filters, setFilters] = useState({
    startDate: initialRange[0].format('YYYY-MM-DD'),
    endDate: initialRange[1].format('YYYY-MM-DD'),
    category: '',
    store: ''
  });
  const [posFilters, setPosFilters] = useState({
    startDate: initialRange[0].format('YYYY-MM-DD'),
    endDate: initialRange[1].format('YYYY-MM-DD'),
    storeId: ''
  });
  const [stores, setStores] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [posAnalytics, setPosAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posLoading, setPosLoading] = useState(false);
  const [showTables, setShowTables] = useState(false);
  const [productInsights, setProductInsights] = useState(null);

  useEffect(() => {
    if (presetFromUrl && DATE_PRESETS.some((p) => p.id === presetFromUrl)) {
      const found = DATE_PRESETS.find((p) => p.id === presetFromUrl);
      const [start, end] = found.range();
      const next = {
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD')
      };
      setPreset(presetFromUrl);
      setFilters((prev) => ({ ...prev, ...next }));
      setPosFilters((prev) => ({ ...prev, ...next }));
    }
  }, [presetFromUrl]);

  const applyPreset = (id, target = 'oms') => {
    const found = DATE_PRESETS.find((p) => p.id === id);
    if (!found) return;
    const [start, end] = found.range();
    const next = {
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD')
    };
    setPreset(id);
    if (target === 'oms') setFilters((prev) => ({ ...prev, ...next }));
    else setPosFilters((prev) => ({ ...prev, ...next }));
  };

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate
      };
      if (filters.category) params.category = filters.category;
      if (filters.store) params.store = filters.store;

      const [analyticsRes, insightsRes] = await Promise.all([
        api.get('/analytics', { params }),
        api.get('/analytics/product-insights', {
          params: { startDate: filters.startDate, endDate: filters.endDate }
        })
      ]);
      setAnalytics(analyticsRes.data);
      setProductInsights(insightsRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalytics(null);
      setProductInsights(null);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchPosAnalytics = useCallback(async () => {
    try {
      setPosLoading(true);
      const params = {
        startDate: posFilters.startDate,
        endDate: posFilters.endDate
      };
      if (posFilters.storeId) params.storeId = posFilters.storeId;
      const response = await api.get('/analytics/pos', { params });
      setPosAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching POS analytics:', error);
      setPosAnalytics(null);
    } finally {
      setPosLoading(false);
    }
  }, [posFilters]);

  useEffect(() => {
    api
      .get('/stores/admin')
      .then(({ data }) => {
        setStores((data.stores || []).filter((s) => Number(s.is_active) === 1));
      })
      .catch(() => setStores([]));
  }, []);

  useEffect(() => {
    if (sourceTab === 'oms') fetchAnalytics();
  }, [sourceTab, fetchAnalytics]);

  useEffect(() => {
    if (sourceTab === 'pos') fetchPosAnalytics();
  }, [sourceTab, fetchPosAnalytics]);

  const handleFilterChange = (field, value) => {
    setPreset('custom');
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handlePosFilterChange = (field, value) => {
    setPreset('custom');
    setPosFilters((prev) => ({ ...prev, [field]: value }));
  };

  const activeRange = sourceTab === 'oms' ? filters : posFilters;
  const periodLabel = useMemo(() => {
    const a = moment(activeRange.startDate);
    const b = moment(activeRange.endDate);
    if (a.isSame(b, 'day')) return formatDateDisplay(a.format('YYYY-MM-DD'));
    return `${formatDateDisplay(a.format('YYYY-MM-DD'))} – ${formatDateDisplay(b.format('YYYY-MM-DD'))}`;
  }, [activeRange]);

  const daySpan = useMemo(() => {
    return Math.max(1, moment(activeRange.endDate).diff(moment(activeRange.startDate), 'days') + 1);
  }, [activeRange]);

  const avgOrderValue = useMemo(() => {
    if (!analytics || analytics.totalOrders === 0) return 0;
    return analytics.totalSales / analytics.totalOrders;
  }, [analytics]);

  const revenueTrendData = useMemo(() => {
    if (!analytics?.dailySummary) return [];
    return Object.entries(analytics.dailySummary)
      .map(([date, d]) => ({
        date,
        label: formatDateDisplay(date).slice(0, 5),
        revenue: parseFloat(d.revenue || 0),
        orders: parseInt(d.order_count || 0, 10)
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [analytics]);

  const categoryChartData = useMemo(() => {
    if (!analytics?.categorySummary) return [];
    return Object.entries(analytics.categorySummary)
      .map(([name, d], i) => ({
        name: name.replace('Store-', 'Store '),
        value: parseFloat(d.revenue || 0),
        orderCount: parseInt(d.order_count || 0, 10),
        fill: PIE_COLORS[i % PIE_COLORS.length]
      }))
      .sort((a, b) => b.value - a.value);
  }, [analytics]);

  const ordersByDayData = useMemo(() => {
    if (!analytics?.dailySummary) return [];
    return Object.entries(analytics.dailySummary)
      .map(([date, d]) => ({
        date,
        label: formatDateDisplay(date).slice(0, 5),
        orders: parseInt(d.order_count || 0, 10)
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [analytics]);

  const omsInsights = useMemo(() => {
    if (!revenueTrendData.length) return null;
    const peak = [...revenueTrendData].sort((a, b) => b.revenue - a.revenue)[0];
    const topCat = categoryChartData[0];
    const avgPerDay = (analytics?.totalSales || 0) / daySpan;
    const ordersPerDay = (analytics?.totalOrders || 0) / daySpan;
    return { peak, topCat, avgPerDay, ordersPerDay };
  }, [revenueTrendData, categoryChartData, analytics, daySpan]);

  const posRevenueTrendData = useMemo(() => {
    if (!posAnalytics?.dailySummary) return [];
    return Object.entries(posAnalytics.dailySummary)
      .map(([date, d]) => ({
        date,
        label: formatDateDisplay(date).slice(0, 5),
        revenue: parseFloat(d.revenue || 0),
        orders: parseInt(d.order_count || 0, 10)
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [posAnalytics]);

  const posStoreChartData = useMemo(() => {
    if (!posAnalytics?.storeSummary) return [];
    return Object.entries(posAnalytics.storeSummary)
      .map(([name, d], i) => ({
        name,
        value: parseFloat(d.revenue || 0),
        orderCount: parseInt(d.order_count || 0, 10),
        fill: PIE_COLORS[i % PIE_COLORS.length]
      }))
      .sort((a, b) => b.value - a.value);
  }, [posAnalytics]);

  const posCategoryChartData = useMemo(() => {
    if (!posAnalytics?.categorySummary) return [];
    return Object.entries(posAnalytics.categorySummary)
      .map(([name, d], i) => ({
        name,
        value: parseFloat(d.revenue || 0),
        lineCount: parseInt(d.line_count || 0, 10),
        fill: PIE_COLORS[i % PIE_COLORS.length]
      }))
      .sort((a, b) => b.value - a.value);
  }, [posAnalytics]);

  const posInsights = useMemo(() => {
    if (!posRevenueTrendData.length) return null;
    const peak = [...posRevenueTrendData].sort((a, b) => b.revenue - a.revenue)[0];
    const topStore = posStoreChartData[0];
    const avgPerDay = (posAnalytics?.totalSales || 0) / daySpan;
    return { peak, topStore, avgPerDay };
  }, [posRevenueTrendData, posStoreChartData, posAnalytics, daySpan]);

  const categoryTotal = categoryChartData.reduce((s, c) => s + c.value, 0) || 1;
  const posCategoryTotal = posCategoryChartData.reduce((s, c) => s + c.value, 0) || 1;

  const flavourRankData = useMemo(
    () =>
      (productInsights?.topFlavours || []).map((r) => ({
        ...r,
        label: r.name.length > 16 ? `${r.name.slice(0, 15)}…` : r.name
      })),
    [productInsights]
  );
  const dessertRankData = useMemo(
    () =>
      (productInsights?.topDesserts || []).map((r) => ({
        ...r,
        label: r.name.length > 16 ? `${r.name.slice(0, 15)}…` : r.name
      })),
    [productInsights]
  );
  const agentRankData = useMemo(
    () =>
      (productInsights?.agentSales || []).map((r) => ({
        ...r,
        label: r.name.length > 14 ? `${r.name.slice(0, 13)}…` : r.name
      })),
    [productInsights]
  );

  return (
    <div className="bi-page">
      <PageNavbar />
      <div className="bi-container">
        <header className="bi-header">
          <div className="bi-header__copy">
            <p className="bi-eyebrow">Business insights</p>
            <h1>Sales performance</h1>
            <p className="bi-header__sub">
              Executive view of bakery orders and counter sales · <strong>{periodLabel}</strong>
            </p>
          </div>
          <div className="bi-header__search">
            <OrderSearch placeholder="Jump to order (CX-1234)" />
          </div>
        </header>

        <div className="bi-toolbar">
          <div className="bi-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={sourceTab === 'oms'}
              className={`bi-tab ${sourceTab === 'oms' ? 'is-active' : ''}`}
              onClick={() => setSourceTab('oms')}
            >
              Bakery orders
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={sourceTab === 'pos'}
              className={`bi-tab ${sourceTab === 'pos' ? 'is-active' : ''}`}
              onClick={() => setSourceTab('pos')}
            >
              POS counter
            </button>
          </div>

          <div className="bi-presets">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`bi-preset ${preset === p.id ? 'is-active' : ''}`}
                onClick={() => applyPreset(p.id, sourceTab)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {sourceTab === 'oms' ? (
          <>
            <div className="bi-filters">
              <label>
                <span>From</span>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
              </label>
              <label>
                <span>To</span>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                />
              </label>
              <label>
                <span>Category</span>
                <select value={filters.category} onChange={(e) => handleFilterChange('category', e.target.value)}>
                  <option value="">All categories</option>
                  <option value="Online Orders">Online Orders</option>
                  <option value="Store-1">Store-1</option>
                  <option value="Store-2">Store-2</option>
                  <option value="Desserts">Desserts</option>
                  <option value="Catering">Catering</option>
                </select>
              </label>
              <label>
                <span>Store</span>
                <select value={filters.store} onChange={(e) => handleFilterChange('store', e.target.value)}>
                  <option value="">All stores</option>
                  <option value="1">Store-1</option>
                  <option value="2">Store-2</option>
                </select>
              </label>
            </div>

            {loading ? (
              <div className="bi-loading">Loading insights…</div>
            ) : analytics ? (
              <>
                <section className="bi-kpi-grid">
                  <article className="bi-kpi bi-kpi--hero">
                    <span className="bi-kpi__label">Total revenue</span>
                    <strong className="bi-kpi__value">{formatCurrency(analytics.totalSales)}</strong>
                    <span className="bi-kpi__meta">{daySpan} day period · OMS</span>
                  </article>
                  <article className="bi-kpi">
                    <span className="bi-kpi__icon"><AppIcon name="orders" size={18} /></span>
                    <span className="bi-kpi__label">Orders</span>
                    <strong className="bi-kpi__value">{analytics.totalOrders}</strong>
                    <span className="bi-kpi__meta">
                      ~{(analytics.totalOrders / daySpan).toFixed(1)} / day
                    </span>
                  </article>
                  <article className="bi-kpi">
                    <span className="bi-kpi__icon"><AppIcon name="analytics" size={18} /></span>
                    <span className="bi-kpi__label">Avg order value</span>
                    <strong className="bi-kpi__value">{formatCurrency(avgOrderValue)}</strong>
                    <span className="bi-kpi__meta">Per ticket</span>
                  </article>
                  <article className="bi-kpi">
                    <span className="bi-kpi__icon"><AppIcon name="currency" size={18} /></span>
                    <span className="bi-kpi__label">Avg daily revenue</span>
                    <strong className="bi-kpi__value">
                      {formatCurrency(omsInsights?.avgPerDay || 0)}
                    </strong>
                    <span className="bi-kpi__meta">Across selected range</span>
                  </article>
                </section>

                {omsInsights && (
                  <section className="bi-insight-strip">
                    <div className="bi-insight">
                      <span>Peak revenue day</span>
                      <strong>
                        {formatDateDisplay(omsInsights.peak.date)} · {formatCurrency(omsInsights.peak.revenue)}
                      </strong>
                    </div>
                    {omsInsights.topCat && (
                      <div className="bi-insight">
                        <span>Top category</span>
                        <strong>
                          {omsInsights.topCat.name} · {formatCurrency(omsInsights.topCat.value)}
                        </strong>
                      </div>
                    )}
                    <div className="bi-insight">
                      <span>Order pace</span>
                      <strong>{omsInsights.ordersPerDay.toFixed(1)} orders / day</strong>
                    </div>
                  </section>
                )}

                <div className="bi-charts">
                  <div className="bi-panel bi-panel--wide">
                    <div className="bi-panel__head">
                      <div>
                        <h3>Revenue trend</h3>
                        <p>Daily OMS revenue for the selected period</p>
                      </div>
                    </div>
                    {revenueTrendData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={revenueTrendData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="biRevenueFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#c9a227" stopOpacity={0.35} />
                              <stop offset="100%" stopColor="#c9a227" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ece7df" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#78716c' }} axisLine={false} tickLine={false} />
                          <YAxis
                            tickFormatter={(v) => (v >= 1000 ? `₹${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `₹${v}`)}
                            tick={{ fontSize: 11, fill: '#78716c' }}
                            axisLine={false}
                            tickLine={false}
                            width={48}
                          />
                          <Tooltip content={<ChartTooltip />} />
                          <Area
                            type="monotone"
                            dataKey="revenue"
                            name="Revenue"
                            stroke="#b8860b"
                            strokeWidth={2.5}
                            fill="url(#biRevenueFill)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="bi-empty">No daily revenue in this range</div>
                    )}
                  </div>

                  <div className="bi-panel">
                    <div className="bi-panel__head">
                      <div>
                        <h3>Orders per day</h3>
                        <p>Volume by delivery date</p>
                      </div>
                    </div>
                    {ordersByDayData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={ordersByDayData} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ece7df" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#78716c' }} axisLine={false} tickLine={false} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#78716c' }} axisLine={false} tickLine={false} width={32} />
                          <Tooltip content={<ChartTooltip />} />
                          <Bar dataKey="orders" name="Orders" fill="#1a1a1a" radius={[4, 4, 0, 0]} maxBarSize={28} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="bi-empty">No order volume in this range</div>
                    )}
                  </div>

                  <div className="bi-panel">
                    <div className="bi-panel__head">
                      <div>
                        <h3>Revenue mix</h3>
                        <p>Share by OMS category</p>
                      </div>
                    </div>
                    {categoryChartData.length > 0 ? (
                      <div className="bi-donut-wrap">
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={categoryChartData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={58}
                              outerRadius={88}
                              paddingAngle={2}
                            >
                              {categoryChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.fill} stroke="#fff" strokeWidth={2} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrencyExact(value)} />
                          </PieChart>
                        </ResponsiveContainer>
                        <ul className="bi-legend">
                          {categoryChartData.map((c) => (
                            <li key={c.name}>
                              <i style={{ background: c.fill }} />
                              <span>{c.name}</span>
                              <strong>{Math.round((c.value / categoryTotal) * 100)}%</strong>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="bi-empty">No category mix yet</div>
                    )}
                  </div>

                  <div className="bi-panel">
                    <div className="bi-panel__head">
                      <div>
                        <h3>Category ranking</h3>
                        <p>Revenue and order count</p>
                      </div>
                    </div>
                    {categoryChartData.length > 0 ? (
                      <div className="bi-rank">
                        {categoryChartData.map((c, i) => (
                          <div key={c.name} className="bi-rank__row">
                            <span className="bi-rank__pos">{i + 1}</span>
                            <div className="bi-rank__body">
                              <div className="bi-rank__top">
                                <strong>{c.name}</strong>
                                <span>{formatCurrency(c.value)}</span>
                              </div>
                              <div className="bi-rank__bar">
                                <span style={{ width: `${(c.value / categoryTotal) * 100}%`, background: c.fill }} />
                              </div>
                              <small>{c.orderCount} orders</small>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bi-empty">No ranking data</div>
                    )}
                  </div>
                </div>

                <div className="bi-section-head">
                  <h2>Product &amp; agent sales</h2>
                  <p>Cake flavours, desserts, and agent performance for the selected dates</p>
                </div>

                <div className="bi-charts">
                  <div className="bi-panel">
                    <div className="bi-panel__head">
                      <div>
                        <h3>Top cake flavours</h3>
                        <p>By line revenue</p>
                      </div>
                    </div>
                    {flavourRankData.length ? (
                      <ResponsiveContainer width="100%" height={Math.max(220, flavourRankData.length * 26)}>
                        <BarChart data={flavourRankData} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ece7df" horizontal={false} />
                          <XAxis
                            type="number"
                            tickFormatter={(v) => (v >= 1000 ? `₹${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `₹${v}`)}
                            tick={{ fontSize: 11, fill: '#78716c' }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis type="category" dataKey="label" width={112} tick={{ fontSize: 11, fill: '#78716c' }} axisLine={false} tickLine={false} />
                          <Tooltip
                            formatter={(value, _n, ctx) => [
                              formatCurrencyExact(value),
                              `${ctx?.payload?.quantity || 0} qty · ${ctx?.payload?.orderCount || 0} orders`
                            ]}
                            labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ''}
                          />
                          <Bar dataKey="revenue" name="Revenue" fill="#b8860b" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="bi-empty">No cake flavour sales in this range</div>
                    )}
                  </div>

                  <div className="bi-panel">
                    <div className="bi-panel__head">
                      <div>
                        <h3>Top desserts</h3>
                        <p>By line revenue</p>
                      </div>
                    </div>
                    {dessertRankData.length ? (
                      <ResponsiveContainer width="100%" height={Math.max(220, dessertRankData.length * 26)}>
                        <BarChart data={dessertRankData} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ece7df" horizontal={false} />
                          <XAxis
                            type="number"
                            tickFormatter={(v) => (v >= 1000 ? `₹${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `₹${v}`)}
                            tick={{ fontSize: 11, fill: '#78716c' }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis type="category" dataKey="label" width={112} tick={{ fontSize: 11, fill: '#78716c' }} axisLine={false} tickLine={false} />
                          <Tooltip
                            formatter={(value, _n, ctx) => [
                              formatCurrencyExact(value),
                              `${ctx?.payload?.quantity || 0} qty · ${ctx?.payload?.orderCount || 0} orders`
                            ]}
                            labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ''}
                          />
                          <Bar dataKey="revenue" name="Revenue" fill="#5c1520" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="bi-empty">No dessert sales in this range</div>
                    )}
                  </div>

                  <div className="bi-panel bi-panel--wide">
                    <div className="bi-panel__head">
                      <div>
                        <h3>Agent-wise sales</h3>
                        <p>Order revenue attributed to each agent</p>
                      </div>
                    </div>
                    {agentRankData.length ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={agentRankData} margin={{ top: 12, right: 12, left: 0, bottom: 48 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ece7df" vertical={false} />
                          <XAxis
                            dataKey="label"
                            angle={-28}
                            textAnchor="end"
                            interval={0}
                            height={60}
                            tick={{ fontSize: 11, fill: '#78716c' }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis
                            tickFormatter={(v) => (v >= 1000 ? `₹${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `₹${v}`)}
                            tick={{ fontSize: 11, fill: '#78716c' }}
                            axisLine={false}
                            tickLine={false}
                            width={48}
                          />
                          <Tooltip
                            formatter={(value, _n, ctx) => [
                              formatCurrencyExact(value),
                              `${ctx?.payload?.orderCount || 0} orders`
                            ]}
                            labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ''}
                          />
                          <Bar dataKey="revenue" name="Revenue" fill="#1a1a1a" radius={[4, 4, 0, 0]} maxBarSize={36} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="bi-empty">No agent sales in this range</div>
                    )}
                  </div>
                </div>

                <div className="bi-detail-toggle">
                  <button type="button" className="bi-btn-ghost" onClick={() => setShowTables(!showTables)}>
                    {showTables ? 'Hide' : 'Show'} daily & monthly detail tables
                  </button>
                </div>

                {showTables && (
                  <div className="bi-tables">
                    <div className="bi-panel">
                      <div className="bi-panel__head"><div><h3>Daily summary</h3></div></div>
                      <div className="bi-table-wrap">
                        <table className="bi-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Orders</th>
                              <th>Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.keys(analytics.dailySummary)
                              .sort((a, b) => new Date(b) - new Date(a))
                              .map((date) => (
                                <tr key={date}>
                                  <td>{formatDateDisplay(date)}</td>
                                  <td>{analytics.dailySummary[date].order_count}</td>
                                  <td>{formatCurrencyExact(analytics.dailySummary[date].revenue)}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="bi-panel">
                      <div className="bi-panel__head"><div><h3>Monthly summary</h3></div></div>
                      <div className="bi-table-wrap">
                        <table className="bi-table">
                          <thead>
                            <tr>
                              <th>Month</th>
                              <th>Orders</th>
                              <th>Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.keys(analytics.monthlySummary)
                              .sort((a, b) => b.localeCompare(a))
                              .map((month) => (
                                <tr key={month}>
                                  <td>{moment(month + '-01').format('MMMM YYYY')}</td>
                                  <td>{analytics.monthlySummary[month].order_count}</td>
                                  <td>{formatCurrencyExact(analytics.monthlySummary[month].revenue)}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bi-empty-card">No OMS data for these filters.</div>
            )}
          </>
        ) : (
          <>
            <div className="bi-filters">
              <label>
                <span>From</span>
                <input
                  type="date"
                  value={posFilters.startDate}
                  onChange={(e) => handlePosFilterChange('startDate', e.target.value)}
                />
              </label>
              <label>
                <span>To</span>
                <input
                  type="date"
                  value={posFilters.endDate}
                  onChange={(e) => handlePosFilterChange('endDate', e.target.value)}
                />
              </label>
              <label>
                <span>Franchise store</span>
                <select value={posFilters.storeId} onChange={(e) => handlePosFilterChange('storeId', e.target.value)}>
                  <option value="">All stores</option>
                  {stores.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {posLoading ? (
              <div className="bi-loading">Loading POS insights…</div>
            ) : posAnalytics ? (
              <>
                <section className="bi-kpi-grid">
                  <article className="bi-kpi bi-kpi--hero">
                    <span className="bi-kpi__label">POS revenue</span>
                    <strong className="bi-kpi__value">{formatCurrency(posAnalytics.totalSales)}</strong>
                    <span className="bi-kpi__meta">{daySpan} day period · counter sales</span>
                  </article>
                  <article className="bi-kpi">
                    <span className="bi-kpi__icon"><AppIcon name="orders" size={18} /></span>
                    <span className="bi-kpi__label">Tickets</span>
                    <strong className="bi-kpi__value">{posAnalytics.totalOrders}</strong>
                    <span className="bi-kpi__meta">
                      ~{(posAnalytics.totalOrders / daySpan).toFixed(1)} / day
                    </span>
                  </article>
                  <article className="bi-kpi">
                    <span className="bi-kpi__icon"><AppIcon name="analytics" size={18} /></span>
                    <span className="bi-kpi__label">Avg ticket</span>
                    <strong className="bi-kpi__value">
                      {formatCurrency(posAnalytics.avgOrderValue || 0)}
                    </strong>
                    <span className="bi-kpi__meta">Per sale</span>
                  </article>
                  <article className="bi-kpi">
                    <span className="bi-kpi__icon"><AppIcon name="currency" size={18} /></span>
                    <span className="bi-kpi__label">Approved expenses</span>
                    <strong className="bi-kpi__value">
                      {formatCurrency(posAnalytics.totalExpenses || 0)}
                    </strong>
                    <span className="bi-kpi__meta">
                      {posAnalytics.expenseCount || 0} expense{(posAnalytics.expenseCount || 0) === 1 ? '' : 's'} · net{' '}
                      {formatCurrency(posAnalytics.netSales != null ? posAnalytics.netSales : (posAnalytics.totalSales || 0) - (posAnalytics.totalExpenses || 0))}
                    </span>
                  </article>
                  <article className="bi-kpi">
                    <span className="bi-kpi__icon"><AppIcon name="currency" size={18} /></span>
                    <span className="bi-kpi__label">Avg daily revenue</span>
                    <strong className="bi-kpi__value">
                      {formatCurrency(posInsights?.avgPerDay || 0)}
                    </strong>
                    <span className="bi-kpi__meta">Across selected range</span>
                  </article>
                </section>

                {posInsights && (
                  <section className="bi-insight-strip">
                    <div className="bi-insight">
                      <span>Peak POS day</span>
                      <strong>
                        {formatDateDisplay(posInsights.peak.date)} · {formatCurrency(posInsights.peak.revenue)}
                      </strong>
                    </div>
                    {posInsights.topStore && (
                      <div className="bi-insight">
                        <span>Top outlet</span>
                        <strong>
                          {posInsights.topStore.name} · {formatCurrency(posInsights.topStore.value)}
                        </strong>
                      </div>
                    )}
                  </section>
                )}

                <div className="bi-charts">
                  <div className="bi-panel bi-panel--wide">
                    <div className="bi-panel__head">
                      <div>
                        <h3>POS revenue trend</h3>
                        <p>Daily counter sales</p>
                      </div>
                    </div>
                    {posRevenueTrendData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={posRevenueTrendData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="biPosFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#c9a227" stopOpacity={0.35} />
                              <stop offset="100%" stopColor="#c9a227" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ece7df" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#78716c' }} axisLine={false} tickLine={false} />
                          <YAxis
                            tickFormatter={(v) => (v >= 1000 ? `₹${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `₹${v}`)}
                            tick={{ fontSize: 11, fill: '#78716c' }}
                            axisLine={false}
                            tickLine={false}
                            width={48}
                          />
                          <Tooltip content={<ChartTooltip />} />
                          <Area
                            type="monotone"
                            dataKey="revenue"
                            name="Revenue"
                            stroke="#b8860b"
                            strokeWidth={2.5}
                            fill="url(#biPosFill)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="bi-empty">No POS sales in this range</div>
                    )}
                  </div>

                  <div className="bi-panel">
                    <div className="bi-panel__head">
                      <div>
                        <h3>Sales by store</h3>
                        <p>Outlet contribution</p>
                      </div>
                    </div>
                    {posStoreChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={posStoreChartData} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ece7df" vertical={false} />
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#78716c' }} axisLine={false} tickLine={false} />
                          <YAxis
                            tickFormatter={(v) => (v >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`)}
                            tick={{ fontSize: 11, fill: '#78716c' }}
                            axisLine={false}
                            tickLine={false}
                            width={48}
                          />
                          <Tooltip formatter={(value) => formatCurrencyExact(value)} />
                          <Bar dataKey="value" name="Revenue" fill="#5c1520" radius={[4, 4, 0, 0]} maxBarSize={40} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="bi-empty">No store data</div>
                    )}
                  </div>

                  <div className="bi-panel">
                    <div className="bi-panel__head">
                      <div>
                        <h3>Menu mix</h3>
                        <p>POS category share</p>
                      </div>
                    </div>
                    {posCategoryChartData.length > 0 ? (
                      <div className="bi-donut-wrap">
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={posCategoryChartData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={58}
                              outerRadius={88}
                              paddingAngle={2}
                            >
                              {posCategoryChartData.map((entry, index) => (
                                <Cell key={`pos-cat-${index}`} fill={entry.fill} stroke="#fff" strokeWidth={2} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrencyExact(value)} />
                          </PieChart>
                        </ResponsiveContainer>
                        <ul className="bi-legend">
                          {posCategoryChartData.map((c) => (
                            <li key={c.name}>
                              <i style={{ background: c.fill }} />
                              <span>{c.name}</span>
                              <strong>{Math.round((c.value / posCategoryTotal) * 100)}%</strong>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="bi-empty">
                        No category breakdown yet. New catalog sales will appear here.
                      </div>
                    )}
                  </div>

                  <div className="bi-panel">
                    <div className="bi-panel__head">
                      <div>
                        <h3>Category ranking</h3>
                        <p>POS menu revenue</p>
                      </div>
                    </div>
                    {posCategoryChartData.length > 0 ? (
                      <div className="bi-rank">
                        {posCategoryChartData.map((c, i) => (
                          <div key={c.name} className="bi-rank__row">
                            <span className="bi-rank__pos">{i + 1}</span>
                            <div className="bi-rank__body">
                              <div className="bi-rank__top">
                                <strong>{c.name}</strong>
                                <span>{formatCurrency(c.value)}</span>
                              </div>
                              <div className="bi-rank__bar">
                                <span style={{ width: `${(c.value / posCategoryTotal) * 100}%`, background: c.fill }} />
                              </div>
                              <small>{c.lineCount} lines</small>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bi-empty">No ranking data</div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="bi-empty-card">No POS data for these filters.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
